// src/lib/deliveryStatus.js
// Prompt 121 ("status 102R") — THE ONE home for the M10 delivery-status rule.
// Every delivery-status pill / stage label / on-time figure on every procurement
// surface derives from HERE. NEVER read raw purchase_detail.receipt_status for a
// user-facing delivery state again (M10 §"DELIVERY STATUS + ON-TIME RATE", ruled
// 2026-07-12 — implemented verbatim below, NOT re-derived).
//
// A delivery state is a PAIR of two INDEPENDENT axes, reported together:
//   AXIS 1 receipt: 'en_route' (nothing) | 'partial' (some, not all) | 'received' (all)
//   AXIS 2 timing:  'on_time' | 'late'
// The order inherits, PER AXIS, the strictest of its lines (the axes never merge).
//
// Data inputs (all present on the line objects the surfaces already have, after the
// Prompt-121 A1 backend add of last_receipt_date):
//   line.receipt_status         — stored receipt axis (Σ goods_receipts.qty ≥ qty at write)
//   line.expected_receipt_date  — the line's ETA (YYYY-MM-DD, plain date) | null
//   line.last_receipt_date      — MAX(goods_receipts.receipt_date) (YYYY-MM-DD) | null
// `today` is a YYYY-MM-DD string (date-granular; both date cols are plain `date`, so
// the '>' compares are lexicographic-safe on ISO dates — no timestamp skew).

// 710: EXACTLY the 3 ruled DELIVERY values. `cancelled` / `partially_cancelled` were ruled OFF this
// axis by 417 (they are DISPOSITION, rendered by orderDispositionBadge) — keys deleted, not renamed.
export const RECEIPT_LABELS = { en_route: 'En Route', partial: 'Partial', received: 'Received' };
export const TIMING_LABELS  = { on_time: 'On Time', late: 'Late' };

// Helper: normalize a date-ish value to a YYYY-MM-DD string (or null).
function ymd(d) {
  if (!d) return null;
  return typeof d === 'string' ? d.slice(0, 10) : new Date(d).toISOString().slice(0, 10);
}

export function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * deriveLineStatus(line, today) → { receipt, timing }
 *  receipt ∈ {en_route, partial, received} — from the stored receipt_status.
 *  timing  ∈ {on_time, late}:
 *    late IF (today > ETA and not fully received)            ← still-outstanding late
 *         OR (fully received but last_receipt_date > ETA)    ← 'received late', PERMANENT
 *    on_time otherwise. A null ETA → NEVER late (cannot score against a missing promise).
 *    LATE THRESHOLD: day AFTER ETA (ETA day itself = on_time), i.e. strict '>'.
 */
export function deriveLineStatus(line, today = todayStr()) {
  // DELIVERY PROGRESS axis (417, STATUS-AXES-ARE-INDEPENDENT-NO-CONFLATION): en_route | partial |
  // received ONLY. cancelled / partially_cancelled are DISPOSITION facts and have LEFT this axis (they
  // now render on the disposition axis, 415). A cancelled line made no live delivery promise and is
  // TERMINAL (short-close = received precedent, isTerminal) → on the pure progress axis it reads
  // 'received' (done, not awaited); its cancellation shows on the disposition axis, not here.
  const cancelled = line?.receipt_status === 'cancelled';
  const receipt = line?.receipt_status === 'received' ? 'received'
    : cancelled ? 'received'                                // 417 — terminal; excluded from progress, carried on disposition
    : line?.receipt_status === 'partial' ? 'partial'
    : 'en_route';
  const eta = ymd(line?.expected_receipt_date);
  const lrd = ymd(line?.last_receipt_date);

  let timing = 'on_time';
  // null ETA → never late; cancelled → never late (made no delivery promise, excluded from timing).
  // Keyed on the RAW receipt_status fact (NOT the derived 'received' above) so timing is byte-unchanged.
  if (eta && !cancelled) {
    if (receipt === 'received') {
      if (lrd && lrd > eta) timing = 'late';           // received late — permanent fact
    } else if (today > eta) {
      timing = 'late';                                 // ETA passed, still outstanding
    }
  }
  return { receipt, timing };
}

/**
 * Terminal/open predicates on the RAW stored receipt_status (single source of truth for
 * "is this line still awaited"). isTerminal = received OR cancelled (done, don't await);
 * isOpen = en_route OR partial (still awaiting). B2.2 routes the raw ternaries through these.
 */
export function isTerminal(line) {
  const s = line?.receipt_status;
  return s === 'received' || s === 'cancelled';
}
export function isOpen(line) {
  const s = line?.receipt_status;
  return s === 'en_route' || s === 'partial';
}

/**
 * deriveOrderStatus(lines, today) → { receipt, timing }
 *  Order = STRICTEST line PER AXIS (the two axes evaluated independently):
 *    receipt: received iff ALL lines received; en_route iff NO line received anything;
 *             else partial (progress anywhere → partial).
 *    timing:  late IF ANY line is late; else on_time.
 */
export function deriveOrderStatus(lines, today = todayStr()) {
  const arr = Array.isArray(lines) ? lines : (lines ? [lines] : []);
  if (arr.length === 0) return { receipt: 'en_route', timing: 'on_time' };

  const derived = arr.map(l => deriveLineStatus(l, today));
  const timing = derived.some(d => d.timing === 'late') ? 'late' : 'on_time';

  // DELIVERY PROGRESS (417): en_route / partial / received over the NON-cancelled (survivor) lines
  // ONLY. A cancelled line is EXCLUDED from the progress DENOMINATOR — keyed on the RAW receipt_status
  // fact, NOT a derived label — so it does not count as un-received and does not block 'received'; a
  // still-open sibling dominates. The order's cancellation shows on the DISPOSITION axis (415), no
  // longer here. An all-cancelled order has no survivors → 'received' (terminal; nothing awaited).
  const survivors = arr.filter(l => l?.receipt_status !== 'cancelled').map(l => deriveLineStatus(l, today));
  if (survivors.length === 0) return { receipt: 'received', timing };

  const allReceived = survivors.every(d => d.receipt === 'received');
  const noneStarted = survivors.every(d => d.receipt === 'en_route');
  const receipt = allReceived ? 'received' : noneStarted ? 'en_route' : 'partial';
  return { receipt, timing };
}

/**
 * resolveDeliveryStatus(engine, fallback) → { receipt, timing }
 *
 * Prefer the engine's order-level axes whenever that axis is present. For surfaces
 * whose engine row has not loaded (or that render a line), retain their displayed
 * fallback pair instead. Callers choose that fallback deliberately: pass a derived
 * line/order pair where one exists, or `{ receipt: null, timing: null }` when the
 * UI is intentionally engine-only. This keeps a filter tied to its badge source.
 */
export function resolveDeliveryStatus(engine, fallback = {}) {
  return {
    receipt: engine?.delivery_status ?? fallback.receipt ?? null,
    timing: engine?.delivery_timing ?? fallback.timing ?? null,
  };
}

/**
 * matchesDeliveryStatus(pair, filters) → boolean
 *
 * `receipt` and `timing` are independent filters. `status` supports the legacy
 * combined picker: `late` targets timing; all other values target receipt.
 */
export function matchesDeliveryStatus(pair, { receipt = '', timing = '', status = '' } = {}) {
  if (receipt && pair?.receipt !== receipt) return false;
  if (timing && pair?.timing !== timing) return false;
  if (status && (status === 'late' ? pair?.timing !== 'late' : pair?.receipt !== status)) return false;
  return true;
}

/**
 * computeSupplierOTD(orders, today) → number | null   (percentage, 0–100)
 *  M10 STANDARD OTD — value-weighted, BINARY per order, ETA-passed denominator:
 *    OTD = Σ(value of orders FULLY received on/before ETA) / Σ(value of orders whose
 *          ETA has PASSED) × 100
 *  - An order enters the DENOMINATOR only once ALL its lines' ETAs have passed (order-
 *    level on-time requires all lines by ETA). Lines with null ETA never pass → keep the
 *    order out of the denominator (in-flight, unscoreable).
 *  - NUMERATOR: order value if the order is fully received AND on_time (no line late).
 *  - value = Σ(line.qty × line.unit_price) over the order's lines.
 *  - EMPTY denominator (no order has all-ETAs-passed) → returns null → render "—"
 *    (NOT 0%, which would falsely imply everyone is late, and NOT NaN).
 *
 * @param orders Array<{ lines: line[] }>  each order carries its purchase_detail lines.
 */
export function computeSupplierOTD(orders, today = todayStr()) {
  let numer = 0;   // value of orders fully received on/before ETA
  let denom = 0;   // value of orders whose ETA has passed (all lines)

  for (const order of (orders || [])) {
    const allLines = order?.lines || [];
    if (!allLines.length) continue;

    // SF110-E2.6-PARTIAL-CANCEL-SCORING (line-level cancelled-exclusion): a cancelled line
    // made no delivery promise — it must not admit/exclude the order via its ETA, nor add
    // value to num/denom. Score the SURVIVING (non-cancelled) lines only; the amended order
    // IS the delivery promise (short-close=received precedent). An all-cancelled order has
    // no survivors → skip it entirely (not in denom, not in numer). This is byte-identical
    // to the prior M10 behaviour for orders with NO cancellations (survivors === all lines).
    const lines = allLines.filter(l => l?.receipt_status !== 'cancelled');
    if (!lines.length) continue;

    // Denominator gate (M10 verbatim): the order enters ONLY once ALL its lines' ETAs
    // have PASSED (today > ETA). A null ETA never passes → order excluded (unscoreable).
    // NOTE: receipt state does NOT admit an order to the denominator — an order received
    // BEFORE its ETA is still excluded until that ETA passes (M10:365-368).
    const allEtasPassed = lines.every(l => {
      const eta = ymd(l?.expected_receipt_date);
      return eta && today > eta;
    });
    if (!allEtasPassed) continue;

    const value = lines.reduce(
      (s, l) => s + (parseFloat(l?.qty) || 0) * (parseFloat(l?.unit_price) || 0), 0);
    denom += value;

    const { receipt, timing } = deriveOrderStatus(lines, today);
    if (receipt === 'received' && timing === 'on_time') numer += value;   // on-time, full
  }

  if (denom === 0) return null;               // empty denominator → caller renders "—"
  return (numer / denom) * 100;
}

/**
 * Display helper: a human pair label, e.g. "Partial · Late" / "Received · On Time".
 * Callers that only want the receipt word can read pair.receipt directly.
 */
export function pairLabel({ receipt, timing }) {
  const r = RECEIPT_LABELS[receipt] || receipt;
  return timing === 'late' ? `${r} · Late` : r;
}
